import { useEffect, useState } from "react";
import { Eye, EyeOff, Plus, ShieldCheck, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Loader from "@/components/ui/loader";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { firstPasswordIssue } from "@/lib/password";

import { superAdminApi, type Org, type OrgAdmin } from "./session";

const EMPTY = { email: "", first_name: "", last_name: "", password: "", phone: "" };

/**
 * Manage the admin accounts for one organization.
 *
 * An org admin signs in at that organization's own admin door
 * (/<slug>/admin/sign-in) and the console shows them only their own
 * organization's users. Listing moderation stays shared — the deal inventory
 * is common to every organization.
 */
export default function OrgAdminsModal({ org, onClose }: { org: Org; onClose: () => void }) {
  const [admins, setAdmins] = useState<OrgAdmin[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    superAdminApi
      .get<OrgAdmin[]>(`/orgs/${org.id}/admins`)
      .then(setAdmins)
      .catch((err) => {
        setAdmins([]);
        setError(err instanceof Error ? err.message : "Failed to load admins");
      });

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setField = (key: keyof typeof EMPTY, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setError("");
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.email.trim() || !form.first_name.trim() || !form.last_name.trim()) {
      setError("Email, first name and last name are required.");
      return;
    }
    // Mirrors the backend's password_strength_error so the admin sees the
    // problem before a round trip.
    const issue = firstPasswordIssue(form.password);
    if (issue) {
      setError(issue);
      return;
    }
    setSaving(true);
    try {
      await superAdminApi.post<OrgAdmin>(`/orgs/${org.id}/admins`, {
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        password: form.password,
        phone: form.phone.trim() || null,
      });
      setForm(EMPTY);
      setShowForm(false);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create admin");
    } finally {
      setSaving(false);
    }
  };

  const doorPath = org.is_default ? "/admin/sign-in" : `/${org.slug}/admin/sign-in`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-8 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-2xl border border-border/80 bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border/60 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">{org.name} — Admins</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Sign in at{" "}
              <code className="rounded bg-muted px-1 py-0.5">{doorPath}</code>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          {admins === null ? (
            <Loader />
          ) : admins.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No admins yet for this organization.
            </p>
          ) : (
            <div className="space-y-2">
              {admins.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-3 rounded-xl border border-border/60 bg-muted/20 p-3"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <ShieldCheck className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">
                      {a.first_name} {a.last_name}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">{a.email}</div>
                  </div>
                  <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[0.58rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    {a.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {!showForm ? (
            <Button type="button" variant="outline" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4" />
              New admin
            </Button>
          ) : (
            <form onSubmit={submit} className="space-y-4 rounded-xl border border-border/60 p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="admin-first">First name</Label>
                  <Input
                    id="admin-first"
                    value={form.first_name}
                    onChange={(e) => setField("first_name", e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="admin-last">Last name</Label>
                  <Input
                    id="admin-last"
                    value={form.last_name}
                    onChange={(e) => setField("last_name", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="admin-email">Email</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Email addresses are unique across all organizations.
                  </p>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="admin-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="admin-password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setField("password", e.target.value)}
                      className="pr-11"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <PasswordRequirements password={form.password} className="pt-1" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="admin-phone">Phone (optional)</Label>
                  <Input
                    id="admin-phone"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                  />
                </div>
              </div>

              {error && <p className="text-sm font-medium text-destructive">{error}</p>}

              <div className="flex gap-2">
                <Button type="submit" disabled={saving}>
                  {saving ? "Creating..." : "Create admin"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setForm(EMPTY);
                    setError("");
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {error && !showForm && <p className="text-sm font-medium text-destructive">{error}</p>}
        </div>
      </div>
    </div>
  );
}
